import { IOnboardingCreateProfile } from '@/@types/Onboarding';
import { toast } from '@/hooks/use-toast';
import { OnboardingService } from '@/services/onboarding.service';
import { AuthService } from '@/services/auth.service';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { cnpj, cpf } from 'cpf-cnpj-validator';
import { isValidHttpUrl, normalizeUrlOnBlur } from '@/utils/normalize';
import { isValidFullName } from '@/utils/isValidFullName';
import { PLAIN_TEXT_PATTERN } from '@/utils/sanitizePlainText';
import { getDefaultCountryAlpha2ByLanguage } from '@/utils/browser-locale-defaults';

export const MAX_AVERAGE_MONTHLY_REVENUE_CENTS = 2147483647;

type UseOnboardingProfileStepProps = {
  onClose: () => void;
  profile: any;
  mode: 'create' | 'edit';
  isProfileRejected?: boolean;
  isOnboarding?: boolean;
  isProfileStepComplete?: boolean;
};

export const useOnboardingProfileStep = ({
  onClose,
  profile,
  mode,
  isProfileRejected = false,
  isOnboarding = false,
  isProfileStepComplete = false,
}: UseOnboardingProfileStepProps) => {
  const { t } = useTranslation(['common', 'validation']);
  const queryClient = useQueryClient();
  const { data: me } = useQuery({
    queryKey: ['user-info'],
    queryFn: async () => await AuthService.userInfo(),
  });
  const browserDefaultCountry = getDefaultCountryAlpha2ByLanguage();
  const displayDefaultCountry = mode === 'edit' ? 'BR' : browserDefaultCountry;

  const normalizeCountryAlpha2 = (raw: unknown, fallback: string) => {
    const v = String(raw ?? '').trim();
    return (v || fallback).toUpperCase();
  };

  const toDateInputValue = (value?: string | null) => {
    if (!value) return '';
    // Evita problemas de timezone: "1996-11-25T02:00:00Z" -> "1996-11-25"
    if (typeof value === 'string' && value.length >= 10)
      return value.slice(0, 10);
    return '';
  };

  const contactFromMe = (
    profileValue?: string | null,
    meValue?: string | null,
  ) => {
    if (!isProfileStepComplete) {
      return String(meValue || profileValue || '').trim();
    }
    return String(profileValue || meValue || '').trim();
  };

  // Se o perfil foi rejeitado, os campos de identidade são opcionais
  const CreateProfileSchema = z
    .object({
      profile_type: isProfileRejected
        ? z.string().optional()
        : z.string().min(1, t('required_field')),
      birth_date: isProfileRejected
        ? z.string().optional()
        : z.string().min(1, t('required_field')),
      full_name: isProfileRejected
        ? z.string().optional()
        : z
            .string()
            .min(1, t('required_field'))
            .refine(isValidFullName, {
              message: t('validation:full_name_required'),
            }),
      phone: z.string().optional(),
      phone_prefix: z.string().optional(),
      heard_about_mundpay:
        !isOnboarding || isProfileRejected
          ? z.string().optional()
          : z
              .string()
              .min(1, t('required_field'))
              .regex(PLAIN_TEXT_PATTERN, t('invalid_characters')),
      average_monthly_revenue:
        !isOnboarding || isProfileRejected
          ? z
              .number()
              .max(
                MAX_AVERAGE_MONTHLY_REVENUE_CENTS,
                t('onboarding:average_monthly_revenue_max'),
              )
              .nullable()
              .optional()
          : z
              .number({
                required_error: t('required_field'),
                invalid_type_error: t('required_field'),
              })
              .positive(t('required_field'))
              .max(
                MAX_AVERAGE_MONTHLY_REVENUE_CENTS,
                t('onboarding:average_monthly_revenue_max'),
              ),
      average_monthly_revenue_currency:
        !isOnboarding || isProfileRejected
          ? z.string().optional()
          : z.string().min(1, t('required_field')),
      product_type_sold:
        !isOnboarding || isProfileRejected
          ? z.string().optional()
          : z
              .string()
              .min(1, t('required_field'))
              .regex(PLAIN_TEXT_PATTERN, t('invalid_characters')),
      personal_document: isProfileRejected
        ? z.string().optional()
        : z.string().min(1, t('required_field')),
      social_name: z.string().nullable(),
      business_document: z.string().nullable(),
      nationality: z.string().optional(),
      residence_country: z.string().optional(),
      company_country: z.string().optional(),
      incorporation_country: z.string().optional(),
      incorporation_date: z.string().optional(),
      company_trade_name: z.string().optional(),
      company_type: z.string().optional(),
      company_industry: z.string().optional(),
      company_website: z.string().optional(),
      company_address: z.string().optional(),
      document_type: z.string().optional(),
      document_issuer_country: z.string().optional(),
      document_expiry_date: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const validateIsoDate = (raw: string) => {
        const [y, m, d] = String(raw || '')
          .split('-')
          .map((p) => Number(p));
        if (!y || !m || !d) return { ok: false as const };
        const date = new Date(y, m - 1, d);
        const ok =
          date instanceof Date &&
          !isNaN(date.getTime()) &&
          date.getFullYear() === y &&
          date.getMonth() === m - 1 &&
          date.getDate() === d;
        return { ok, y, m, d, date };
      };

      const nationality = (data.nationality ?? 'BR').toUpperCase();
      const residenceCountry = (data.residence_country ?? 'BR').toUpperCase();
      const companyCountry = (
        data.company_country ?? residenceCountry
      ).toUpperCase();

      // Empresa: validar sempre (inclusive perfil reprovado ao trocar tipo)
      if (data.profile_type === 'business') {
        const businessDoc = (data.business_document || '').trim();
        if (!businessDoc) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('required_field'),
            path: ['business_document'],
          });
        } else if (companyCountry === 'BR') {
          const businessDocDigits = businessDoc.replace(/\D/g, '');
          if (!cnpj.isValid(businessDocDigits)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('common:invalid_document'),
              path: ['business_document'],
            });
          }
        }

        if (companyCountry !== 'BR') {
          const legalName = (data.social_name || '').trim();
          if (!legalName) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('required_field'),
              path: ['social_name'],
            });
          }

          const companyAddress = (data.company_address || '').trim();
          if (!companyAddress) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('required_field'),
              path: ['company_address'],
            });
          }

          if (!data.incorporation_country) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('required_field'),
              path: ['incorporation_country'],
            });
          }

          if (!data.incorporation_date) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('required_field'),
              path: ['incorporation_date'],
            });
          } else {
            const parsed = validateIsoDate(
              String(data.incorporation_date || ''),
            );
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const incAtStartOfDay = parsed.ok
              ? new Date(parsed.date)
              : new Date('invalid');
            incAtStartOfDay.setHours(0, 0, 0, 0);

            if (!parsed.ok || parsed.y < 1900 || incAtStartOfDay > today) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('invalid_date'),
                path: ['incorporation_date'],
              });
            }
          }

          if (!data.company_type) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('required_field'),
              path: ['company_type'],
            });
          }

          if (!data.company_industry) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('required_field'),
              path: ['company_industry'],
            });
          }

          if (data.company_website) {
            const normalizedWebsite = normalizeUrlOnBlur(
              String(data.company_website).trim().replace(/\s/g, ''),
            );
            if (!isValidHttpUrl(normalizedWebsite)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('invalid_url'),
                path: ['company_website'],
              });
            }
          }
        }
      }

      if (isProfileRejected) return;

      // Validação de datas: mesma base do birth_date (data real, ano >= 1900)
      if (data.birth_date) {
        const parsed = validateIsoDate(data.birth_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (!parsed.ok || (parsed.y ?? 0) < 1900) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('invalid_date'),
            path: ['birth_date'],
          });
        } else {
          const birthAtStartOfDay = new Date(parsed.date ?? '');
          birthAtStartOfDay.setHours(0, 0, 0, 0);

          if (birthAtStartOfDay > today) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('invalid_date'),
              path: ['birth_date'],
            });
          } else {
            const minDate = new Date(
              today.getFullYear() - 18,
              today.getMonth(),
              today.getDate(),
            );
            minDate.setHours(0, 0, 0, 0);

            if (birthAtStartOfDay > minDate) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('must_be_18_or_older'),
                path: ['birth_date'],
              });
            }
          }
        }
      }

      // Valida CPF apenas para brasileiros
      if (data.profile_type === 'individual' && nationality === 'BR') {
        const personalDocDigits = (data.personal_document || '').replace(
          /\D/g,
          '',
        );
        if (!cpf.isValid(personalDocDigits)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('common:invalid_document'),
            path: ['personal_document'],
          });
        }
      }

      // Para não-brasileiros, exige campos de compliance no lugar do CPF mascarado
      if (nationality !== 'BR') {
        if (!data.document_type) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('required_field'),
            path: ['document_type'],
          });
        }

        if (!data.document_issuer_country) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('required_field'),
            path: ['document_issuer_country'],
          });
        }

        if (!data.document_expiry_date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('required_field'),
            path: ['document_expiry_date'],
          });
        } else {
          const parsed = validateIsoDate(
            String(data.document_expiry_date || ''),
          );
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const expiryAtStartOfDay = parsed.ok
            ? new Date(parsed.date ?? '')
            : new Date('invalid');
          expiryAtStartOfDay.setHours(0, 0, 0, 0);

          if (
            !parsed.ok ||
            (parsed.y ?? 0) < 1900 ||
            expiryAtStartOfDay < today
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('invalid_date'),
              path: ['document_expiry_date'],
            });
          }
        }

        const docNumber = (data.personal_document || '')
          .trim()
          .replace(/\s/g, '');
        if (!docNumber || docNumber.length < 3) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('required_field'),
            path: ['personal_document'],
          });
        } else if (docNumber.length > 50) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validationUrlTooLong'),
            path: ['personal_document'],
          });
        }
      }

      // Valida CNPJ apenas quando empresa BR
      if (data.profile_type === 'business') {
        const businessDoc = (data.business_document || '').trim();
        if (!businessDoc) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('required_field'),
            path: ['business_document'],
          });
        } else if (companyCountry === 'BR') {
          const businessDocDigits = businessDoc.replace(/\D/g, '');
          if (!cnpj.isValid(businessDocDigits)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('common:invalid_document'),
              path: ['business_document'],
            });
          }
        }

        // Exigências para empresa internacional (país da empresa != BR)
        if (companyCountry !== 'BR') {
          const legalName = (data.social_name || '').trim();
          if (!legalName) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('required_field'),
              path: ['social_name'],
            });
          }

          const companyAddress = (data.company_address || '').trim();
          if (!companyAddress) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('required_field'),
              path: ['company_address'],
            });
          }

          if (!data.incorporation_country) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('required_field'),
              path: ['incorporation_country'],
            });
          }

          if (!data.incorporation_date) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('required_field'),
              path: ['incorporation_date'],
            });
          } else {
            const parsed = validateIsoDate(
              String(data.incorporation_date || ''),
            );
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const incAtStartOfDay = parsed.ok
              ? new Date(parsed.date ?? '')
              : new Date('invalid');
            incAtStartOfDay.setHours(0, 0, 0, 0);

            if (
              !parsed.ok ||
              (parsed.y ?? 0) < 1900 ||
              incAtStartOfDay > today
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('invalid_date'),
                path: ['incorporation_date'],
              });
            }
          }

          if (!data.company_type) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('required_field'),
              path: ['company_type'],
            });
          }

          if (!data.company_industry) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('required_field'),
              path: ['company_industry'],
            });
          }

          if (data.company_website) {
            const normalizedWebsite = normalizeUrlOnBlur(
              String(data.company_website).trim().replace(/\s/g, ''),
            );
            if (!isValidHttpUrl(normalizedWebsite)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('invalid_url'),
                path: ['company_website'],
              });
            }
          }
        }
      }
    });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    watch,
    clearErrors,
    reset,
    resetField,
    trigger,
  } = useForm<IOnboardingCreateProfile>({
    shouldUnregister: false,
    resolver: zodResolver(CreateProfileSchema),
    defaultValues: {
      profile_type: profile?.profile_type || '',
      birth_date: profile?.user?.birth_date || '',
      full_name: contactFromMe(profile?.user?.name, me?.name),
      phone: contactFromMe(profile?.user?.phone, me?.phone),
      phone_prefix: contactFromMe(
        profile?.user?.phone_prefix,
        me?.phone_prefix,
      ),
      heard_about_mundpay: profile?.heard_about_mundpay || '',
      average_monthly_revenue:
        typeof profile?.average_monthly_revenue === 'number'
          ? profile.average_monthly_revenue
          : null,
      average_monthly_revenue_currency:
        profile?.average_monthly_revenue_currency || 'USD',
      product_type_sold: profile?.product_type_sold || '',
      personal_document:
        profile?.documentNumbers?.find(
          (d: any) => d.document_type === 'individual',
        )?.document_value || '',
      social_name: profile?.identifier_name || null,
      business_document:
        profile?.documentNumbers?.find(
          (d: any) => d.document_type === 'business',
        )?.document_value || null,
      nationality: normalizeCountryAlpha2(profile?.user?.nationality, 'BR'),
      residence_country: (
        profile?.user?.residence_country || displayDefaultCountry
      ).toUpperCase(),
      company_country: normalizeCountryAlpha2(
        profile?.company_country || profile?.user?.residence_country,
        'BR',
      ),
      incorporation_country: (
        profile?.incorporation_country ||
        profile?.company_country ||
        displayDefaultCountry
      ).toUpperCase(),
      incorporation_date: toDateInputValue(
        profile?.incorporation_date || profile?.incorporationDate,
      ),
      company_trade_name:
        profile?.company_trade_name || profile?.companyTradeName || '',
      company_type: profile?.company_type || profile?.companyType || '',
      company_industry:
        profile?.company_industry || profile?.companyIndustry || '',
      company_website:
        profile?.company_website || profile?.companyWebsite || '',
      company_address:
        profile?.company_address || (profile as any)?.companyAddress || '',
      document_type:
        profile?.documentNumbers?.find(
          (d: any) => d.document_type === 'individual',
        )?.document_subtype || '',
      document_issuer_country: (
        profile?.documentNumbers?.find(
          (d: any) => d.document_type === 'individual',
        )?.issuer_country || displayDefaultCountry
      ).toUpperCase(),
      document_expiry_date: toDateInputValue(
        profile?.documentNumbers?.find(
          (d: any) => d.document_type === 'individual',
        )?.expiry_date,
      ),
    },
  });

  const defaultValues = watch();

  const { mutate, isPending: isCreatingProfile } = useMutation({
    mutationFn: async (data: IOnboardingCreateProfile) => {
      if (mode === 'create') {
        await OnboardingService.createProfile(data);
      } else {
        await OnboardingService.updateProfile(data);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      await queryClient.invalidateQueries({ queryKey: ['user-info'] });

      toast({ description: t('data_saved'), variant: 'success' });
      onClose();
    },
    onError: (err: any) => {
      if (err?.response?.data?.errors) {
        const errors = err.response.data.errors;
        Object.entries(errors).forEach(([key, message]) => {
          setError(key as keyof IOnboardingCreateProfile, {
            type: 'manual',
            message: String(message),
          });
        });

        // Toast consolidado ao inv\u00e9s de m\u00faltiplos
        const errorCount = Object.keys(errors).length;
        toast({
          description: `${errorCount} erro(s) encontrado(s). Verifique os campos destacados.`,
          variant: 'error',
        });
      } else {
        toast({ description: t('common:unexpected_error'), variant: 'error' });
      }
    },
  });

  const onSubmit = (data: IOnboardingCreateProfile) => {
    const cleanedData = Object.fromEntries(
      Object.entries(data).filter(
        ([_, value]) => value !== null && value !== '',
      ),
    );

    if ((cleanedData as any).company_website) {
      (cleanedData as any).company_website = normalizeUrlOnBlur(
        String((cleanedData as any).company_website)
          .trim()
          .replace(/\s/g, ''),
      );
    }

    const companyCountry = String(
      (cleanedData as any)?.company_country ?? 'BR',
    ).toUpperCase();
    if (companyCountry === 'BR') {
      delete (cleanedData as any).company_trade_name;
      delete (cleanedData as any).company_address;
    }

    mutate(cleanedData as IOnboardingCreateProfile);
  };

  useEffect(() => {
    if (profile) {
      setValue('profile_type', profile.profile_type || '');
      setValue(
        'birth_date',
        profile.user?.birth_date
          ? format(new Date(profile.user.birth_date), 'yyyy-MM-dd')
          : '',
      );
      setValue('heard_about_mundpay', profile?.heard_about_mundpay || '');
      setValue(
        'average_monthly_revenue',
        typeof profile?.average_monthly_revenue === 'number'
          ? profile.average_monthly_revenue
          : null,
      );
      setValue(
        'average_monthly_revenue_currency',
        profile?.average_monthly_revenue_currency || 'USD',
      );
      setValue('product_type_sold', profile?.product_type_sold || '');
      setValue(
        'personal_document',
        profile?.documentNumbers?.find(
          (d: any) => d.document_type === 'individual',
        )?.document_value || '',
      );
      setValue('social_name', profile.identifier_name || '');
      setValue(
        'business_document',
        profile?.documentNumbers?.find(
          (d: any) => d.document_type === 'business',
        )?.document_value || '',
      );
      setValue(
        'company_trade_name',
        profile?.company_trade_name || profile?.companyTradeName || '',
      );
      setValue(
        'incorporation_date',
        toDateInputValue(
          profile?.incorporation_date || profile?.incorporationDate,
        ),
      );
      setValue(
        'company_type',
        profile?.company_type || profile?.companyType || '',
      );
      setValue(
        'company_industry',
        profile?.company_industry || profile?.companyIndustry || '',
      );
      setValue(
        'company_website',
        profile?.company_website || profile?.companyWebsite || '',
      );
      setValue(
        'company_address',
        profile?.company_address || (profile as any)?.companyAddress || '',
      );

      if (profile?.user?.nationality) {
        setValue(
          'nationality',
          String(profile.user?.nationality).toUpperCase(),
        );
      } else if (mode === 'edit') {
        setValue('nationality', 'BR');
      }
      if (profile?.user?.residence_country) {
        setValue(
          'residence_country',
          String(profile.user?.residence_country).toUpperCase(),
        );
      } else if (mode === 'edit') {
        setValue('residence_country', 'BR');
      }
      if (profile?.company_country) {
        setValue(
          'company_country',
          String(profile.company_country).toUpperCase(),
        );
      } else if (mode === 'edit') {
        setValue('company_country', 'BR');
      }
      if (profile?.incorporation_country) {
        setValue(
          'incorporation_country',
          String(profile.incorporation_country).toUpperCase(),
        );
      } else if (mode === 'edit') {
        setValue('incorporation_country', 'BR');
      }

      const individualDoc = profile?.documentNumbers?.find(
        (d: any) => d.document_type === 'individual',
      );

      if (individualDoc?.issuer_country) {
        setValue(
          'document_issuer_country',
          String(individualDoc.issuer_country).toUpperCase(),
        );
      } else if (mode === 'edit') {
        setValue('document_issuer_country', 'BR');
      }

      if (individualDoc?.document_subtype) {
        setValue('document_type', String(individualDoc.document_subtype));
      }

      if (individualDoc?.expiry_date) {
        setValue(
          'document_expiry_date',
          toDateInputValue(String(individualDoc.expiry_date)),
        );
      }
    }
  }, [mode, profile, setValue]);

  useEffect(() => {
    setValue('full_name', contactFromMe(profile?.user?.name, me?.name));
    setValue('phone', contactFromMe(profile?.user?.phone, me?.phone));
    setValue(
      'phone_prefix',
      contactFromMe(profile?.user?.phone_prefix, me?.phone_prefix),
    );
  }, [
    isProfileStepComplete,
    me?.name,
    me?.phone,
    me?.phone_prefix,
    profile?.user?.name,
    profile?.user?.phone,
    profile?.user?.phone_prefix,
    setValue,
  ]);

  useEffect(() => {
    if (defaultValues.profile_type) {
      clearErrors('profile_type');
    }
  }, [defaultValues.profile_type]);

  return {
    register,
    handleSubmit,
    errors,
    setValue,
    setError,
    clearErrors,
    watch,
    trigger,
    defaultValues,
    onSubmit,
    isCreatingProfile,
    reset,
    resetField,
  };
};
